// src/components/qa/PlatformQaPreview.js
//
// Pass 87.1 — PLATFORM QA / DEMO PREVIEW hub (route `/qa/preview`). A platform-only capability for inspecting the
// Operations / Instructor / Student experiences for QA/demo. It is VIEWER CONTEXT, never identity or authority:
//   • gated ONLY by `canUsePlatformQaPreview` (platform super-admin) — ordinary SAS owner/admin/ops/instructor can
//     NEVER see it or reach it by typing the URL (the guard redirects them);
//   • the operator's own identity is always retained — no impersonation, no Auth user is created/altered;
//   • every preview is READ-ONLY (no write acts "as" the previewed user); Firestore still enforces each read;
//   • the selected org/member/participant ids are SELECTIONS, not access grants;
//   • a small "QA Preview — read only" banner shows ONLY here (never in normal workspaces, never to SAS staff).

import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { db } from '../../firebase';
import { useAuth } from '../../AuthContext';
import { canUsePlatformQaPreview } from '../../utils/platformAccess';
import { getOrganizationMemberships } from '../../utils/organizationContext';
import { loadParticipantPreviewCandidates } from '../../utils/studentPreviewCandidates';
import { loadOrganizationMembers } from '../../utils/organizationData';
import { useInstructorLessons } from '../../hooks/useInstructorLessons';
import StudentExperience from '../student/StudentExperience';

const font = "'DM Sans',system-ui,sans-serif";
const C = { ink: '#1a1917', ink2: '#57564f', ink3: '#8a8980', border: '#e7e5e0', bg: '#f4f4f2', card: '#fff', qa: '#9333EA', qaBg: '#F5E8FF' };
const isTeaching = (m) => m && (m.isTeachingInstructor === true || (m.seasonMemberships && Object.values(m.seasonMemberships).some((s) => s && s.isTeachingInstructor === true)));
const memberName = (m) => (m && (m.name || m.displayName)) || (m && (m.userId || m.id)) || 'Member';

function Banner() {
  return (
    <div data-testid="qa-banner" style={{ background: C.qaBg, border: `1px solid ${C.qa}`, color: C.qa, borderRadius: 8, padding: '6px 12px', fontSize: 12.5, fontWeight: 800, letterSpacing: 0.3, display: 'inline-block' }}>
      QA Preview — read only
    </div>
  );
}

// Read-only instructor inspector (thin wrapper — the instructor shell has no read-only mode; §4). Renders the
// selected canonical teaching member's lessons READ-ONLY; NO instructor write actions are offered.
function InstructorQaView({ organizationId, member }) {
  const lessons = useInstructorLessons('', organizationId, member?.userId || member?.id || '');
  const rows = Array.isArray(lessons) ? lessons : (lessons?.lessons || []);
  return (
    <div data-testid="qa-instructor" style={{ marginTop: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{memberName(member)} <span style={{ color: C.ink3, fontWeight: 500 }}>— read-only instructor preview</span></div>
      <div style={{ fontSize: 12.5, color: C.ink2, marginTop: 4 }}>Teaching member of {organizationId}. No instructor actions (lesson completion / notes) are available in QA preview.</div>
      <div style={{ marginTop: 10, fontSize: 13, color: C.ink2 }}>Lessons (read-only): {rows.length}</div>
      <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12.5, color: C.ink2 }}>
        {rows.slice(0, 20).map((l, i) => <li key={l.id || i}>{(l.lessonDate || l.scheduledAt || '—')}{l.discipline ? ` · ${l.discipline}` : ''}{l.title ? ` · ${l.title}` : ''}</li>)}
        {rows.length === 0 ? <li style={{ color: C.ink3, listStyle: 'none', marginLeft: -18 }}>No lessons for this member yet.</li> : null}
      </ul>
    </div>
  );
}

export default function PlatformQaPreview() {
  const { userData } = useAuth() || {};
  const allowed = canUsePlatformQaPreview(userData || {});

  const orgs = useMemo(
    () => getOrganizationMemberships(userData || {}).map((m) => ({ organizationId: m.organizationId, organizationName: m.organizationName || m.organizationId })),
    [userData],
  );
  const [mode, setMode] = useState('student');
  const [orgId, setOrgId] = useState('');
  const [participants, setParticipants] = useState(null);
  const [participantId, setParticipantId] = useState('');
  const [members, setMembers] = useState(null);
  const [memberId, setMemberId] = useState('');

  useEffect(() => { if (!orgId && orgs[0]) setOrgId(orgs[0].organizationId); }, [orgs, orgId]);

  // Load read-only selection data for the chosen org (identity unchanged; Firestore enforces access).
  useEffect(() => {
    let alive = true;
    setParticipants(null); setParticipantId(''); setMembers(null); setMemberId('');
    if (!orgId || !allowed) return undefined;
    (async () => {
      try {
        const [cands, mem] = await Promise.all([
          loadParticipantPreviewCandidates(db, orgId).catch(() => []),
          loadOrganizationMembers(db, orgId).catch(() => []),
        ]);
        if (!alive) return;
        setParticipants(Array.isArray(cands) ? cands : []);
        setMembers((Array.isArray(mem) ? mem : []).filter(isTeaching));
      } catch (_) { if (alive) { setParticipants([]); setMembers([]); } }
    })();
    return () => { alive = false; };
  }, [orgId, allowed]);

  // Guard: platform QA authority ONLY. Route params/selections are never authorization.
  if (!allowed) return <Navigate to="/" replace />;

  const orgLabel = (id) => orgs.find((o) => o.organizationId === id)?.organizationName || id;
  const selectedParticipant = (participants || []).find((p) => p.participantId === participantId) || null;
  const selectedMember = (members || []).find((m) => (m.userId || m.id) === memberId) || null;
  const sel = { fontFamily: font, fontSize: 13, padding: '6px 10px', borderRadius: 8, border: `1px solid ${C.border}`, background: '#fff', color: C.ink };

  return (
    <div data-testid="platform-qa-preview" style={{ fontFamily: font, color: C.ink, background: C.bg, minHeight: '100%', padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 22, fontWeight: 900, margin: 0 }}>QA Preview</h1>
        <Banner />
      </div>
      <p style={{ fontSize: 13, color: C.ink2, maxWidth: 680 }}>Platform QA/demo inspection of the Operations, Instructor and Client/Student experiences. Read-only; your own identity is retained; selections never grant access.</p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        {[['student', 'Client / Student'], ['instructor', 'Instructor'], ['operations', 'Operations']].map(([k, label]) => (
          <button key={k} data-testid={`qa-mode-${k}`} onClick={() => setMode(k)} style={{ ...sel, cursor: 'pointer', fontWeight: mode === k ? 800 : 500, background: mode === k ? C.qaBg : '#fff', borderColor: mode === k ? C.qa : C.border }}>{label}</button>
        ))}
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: C.ink3 }}>Org</span>
          <select data-testid="qa-org" value={orgId} onChange={(e) => setOrgId(e.target.value)} style={sel}>
            {orgs.map((o) => <option key={o.organizationId} value={o.organizationId}>{o.organizationName}</option>)}
          </select>
        </label>
      </div>

      <div style={{ marginTop: 16, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
        {mode === 'student' && (
          <>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: C.ink3 }}>Participant</span>
              <select data-testid="qa-participant" value={participantId} onChange={(e) => setParticipantId(e.target.value)} style={sel}>
                <option value="">{participants === null ? 'Loading…' : (participants.length ? 'Select a participant…' : 'No participants')}</option>
                {(participants || []).map((p) => <option key={p.participantId} value={p.participantId}>{p.name}{p.accountLinked ? '' : ' · (no login yet)'}</option>)}
              </select>
            </label>
            <div style={{ marginTop: 12 }}>
              {selectedParticipant
                ? <StudentExperience participantId={selectedParticipant.participantId} organizationId={orgId} studentName={selectedParticipant.name} viewerMode="staff_preview" />
                : <div data-testid="qa-student-empty" style={{ color: C.ink3, fontSize: 13 }}>No coaching participant selected in <strong>{orgLabel(orgId)}</strong>. Participants are created in the client CRM.</div>}
            </div>
          </>
        )}
        {mode === 'instructor' && (
          <>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: C.ink3 }}>Instructor</span>
              <select data-testid="qa-member" value={memberId} onChange={(e) => setMemberId(e.target.value)} style={sel}>
                <option value="">{members === null ? 'Loading…' : (members.length ? 'Select a teaching member…' : 'No teaching members')}</option>
                {(members || []).map((m) => <option key={m.userId || m.id} value={m.userId || m.id}>{memberName(m)}</option>)}
              </select>
            </label>
            {selectedMember
              ? <InstructorQaView organizationId={orgId} member={selectedMember} />
              : <div data-testid="qa-instructor-empty" style={{ color: C.ink3, fontSize: 13, marginTop: 12 }}>Select a canonical teaching member in <strong>{orgLabel(orgId)}</strong> to preview their read-only instructor view.</div>}
          </>
        )}
        {mode === 'operations' && (
          <div data-testid="qa-operations" style={{ fontSize: 13, color: C.ink2 }}>
            Operations preview uses your REAL authorized organization context for <strong>{orgLabel(orgId)}</strong> (platform QA does not invent operational membership). Open the live Operations workspace:
            <div style={{ marginTop: 8 }}>
              <a href="/organization" style={{ color: C.qa, fontWeight: 700 }}>Open Operations →</a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
